import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Custom hook that scrolls to top when route changes
 * Also provides manual scroll to top functionality
 */
export const useScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // Scroll to top when route changes
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, [pathname]);

  // Manual scroll to top function
  const scrollToTop = (behavior: ScrollBehavior = 'smooth') => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior
    });
  };

  return { scrollToTop };
};

/**
 * Utility function to scroll to top - can be used outside of React components
 */
export const scrollToTop = (behavior: ScrollBehavior = 'smooth') => {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior
  });
};

/**
 * Utility function to scroll to a specific element
 */
export const scrollToElement = (
  elementId: string, 
  behavior: ScrollBehavior = 'smooth',
  offset: number = 0
) => {
  const element = document.getElementById(elementId);
  if (element) {
    const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
    const offsetPosition = elementPosition - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior
    });
  }
};

/**
 * Hook for scrolling to elements with optional offset (useful for fixed headers)
 */
export const useScrollToElement = (offset: number = 0) => {
  const scrollTo = (elementId: string, behavior: ScrollBehavior = 'smooth') => {
    scrollToElement(elementId, behavior, offset);
  };

  return { scrollTo };
};